import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {   ArrowLeftOutlined } from '@ant-design/icons';
import { useSecureService } from '../../store/service';
import { useAuth } from '../../hooks/useAuth';
import { Button, message} from 'antd';
import { useLanguage } from '../../contexts/LanguageContext';
import styles from './Projects.module.scss';
import { getStatusLabel, getStatusColor } from '../../utils/statusHelpers';
import { ReactComponent as ImagePlaceholder } from '../../images/image-placeholder.svg';
import FolderCard from '../../components/FolderCard/FolderCard';
import FooterActions from './FooterActions';

interface FileVersionsProps {
  fileDetail?: any;
  projectName?: string;
  onBack?: () => void;
}

const FileVersions: React.FC<FileVersionsProps> = ({ fileDetail: propFileDetail, projectName: propProjectName, onBack }) => {
  const { t } = useLanguage();
  const { fileId } = useParams<{ fileId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { getFileById, getEntryByPath, deleteEntry } = useSecureService();
  
  const [fileDetail, setFileDetail] = useState<any>(propFileDetail || null);
  const [loading, setLoading] = useState(!propFileDetail);
  const [selectedItems, setSelectedItems] = useState<Set<number>>(new Set());
  const getProjectsBasePath = () => {
    const fromState = (location.state as any)?.fromProjectsBasePath;
    if (fromState === '/internal-projects' || fromState === '/projects' || fromState === '/external-projects') {
      return fromState;
    }
    const saved = localStorage.getItem('lastProjectsBasePath');
    if (saved === '/internal-projects' || saved === '/projects' || saved === '/external-projects') {
      return saved;
    }
    return '/internal-projects';
  };
  



  useEffect(() => {
    const fetchFileDetail = async () => {
      // Eğer propFileDetail varsa onu kullan
      if (propFileDetail) {
        setFileDetail(propFileDetail);
        setLoading(false);

        

      } else {
        // fileId varsa onu kullan, yoksa projectName ve dosya adından bul
        if (fileId) {
          setLoading(true);
          try {
            const response = await getFileById(parseInt(fileId));
            
                          if (response.success && response.data) {
                setFileDetail(response.data);
                

              } else {
              setFileDetail(null);
            }
          } catch (error) {
            setFileDetail(null);
          } finally {
            setLoading(false);
          }
        } else if (propProjectName) {
          // fileId yoksa, URL'den dosya adını çıkar ve getEntryByPath ile bul
          setLoading(true);
          try {
            const pathParts = window.location.pathname.split('/').filter(p => p && p.trim() !== '');
            
            // /versions varsa son elemanı çıkar
            let cleanPathParts = [...pathParts];
            if (cleanPathParts[cleanPathParts.length - 1] === 'versions') {
              cleanPathParts = cleanPathParts.slice(0, -1);
            }
            
            // Tüm path'i birleştir ve getEntryByPath ile dene
            const fullPath = cleanPathParts.join('/');
              
            const response = await getEntryByPath(fullPath);
              
              if (response.success && response.data) {
              const entryData = response.data;
              
              // entryType kontrolü yap
              const entryType = entryData.entryType;
                
              // Eğer bu bir dosya ise (entryType === 'File')
              if (entryType === 'File') {
                if (entryData.id) {
                    try {
                    const fileResponse = await getFileById(entryData.id);
                                              if (fileResponse.success && fileResponse.data) {
                          setFileDetail(fileResponse.data);

                        } else {
                      setFileDetail(entryData);
                      }
                    } catch (error) {
                    setFileDetail(entryData);
                  }
                } else {
                  setFileDetail(entryData);
                }
              } else {
                // Folder ise veya başka bir tip ise
                setFileDetail(null);
              }
            } else {
              setFileDetail(null);
            }
          } catch (error) {
            setFileDetail(null);
          } finally {
            setLoading(false);
          }
        } else {
          setLoading(false);
        }
      }
    };
    fetchFileDetail();
  }, [propFileDetail, fileId, propProjectName]);

  const handleGoBack = () => {
    if (onBack) {
      onBack();
    } else {
      // ID servisinden gelen path'i trim et
      if (fileDetail && fileDetail.path) {
        const pathParts = fileDetail.path.split('/');
        
        
        // Son segment'i çıkar (file-path), sadece proje adını bırak
        if (pathParts.length >= 2) {
          const projectPath = `/${pathParts[0]}`; // path-test-200825
          navigate(projectPath);
        } else {
          // Fallback: proje ana sayfasına git
          navigate(getProjectsBasePath());
        }
      } else {
        // Fallback: proje ana sayfasına git
        navigate(getProjectsBasePath());
      }
    }
  };

  const handleVersionClick = (version: any) => {
    // FileDetail sayfasına yönlendir ve hash ile versiyon seç (path-based)
    // Base URL + path kullan
    const baseUrl = window.location.origin;
    // Path'in başında slash yoksa ekle
    const cleanPath = version.path.startsWith('/') ? version.path : `/${version.path}`;
    const versionPath = `${baseUrl}${cleanPath}`;
    window.location.href = versionPath;
  };



  // Toplu seçim yetkisi kontrolü - sadece admin, super admin ve account owner
  const canMultiSelect = () => {
    if (!user) return false;
    return ['Admin', 'SuperAdmin', 'AccountOwner'].includes(user.role);
  };

  const handleItemSelect = (itemId: number, checked: boolean) => {
    if (checked) {
      // Toplu seçim kontrolü - sadece yetkili roller birden fazla seçim yapabilir
      if (selectedItems.size > 0 && !canMultiSelect()) {
        // Yetkisiz kullanıcı zaten bir öğe seçiliyse yeni seçim yapamaz
        message.warning('You can only make single selections. You do not have permission for multi-selection.');
        return;
      }
      
      const newSelectedItems = new Set(selectedItems);
      newSelectedItems.add(itemId);
      setSelectedItems(newSelectedItems);
    } else {
      const newSelectedItems = new Set(selectedItems);
      newSelectedItems.delete(itemId);
      setSelectedItems(newSelectedItems);
    }
  };



  const handleBulkDelete = async () => {
    try {
      if (selectedItems.size > 0) {
        
        // Seçili versiyonların detaylarını al
        const selectedVersionsDetails = fileDetail?.versions
          ?.filter((version: any) => selectedItems.has(version.id))
          .map((version: any) => ({
            id: version.id,
            entryType:'Version' // API'den gelen gerçek entryType
          })) || [];
        
        if (selectedVersionsDetails.length === 0) {
          throw new Error('No versions to delete');
        }
        
        // deleteEntry API çağrısı yap
        const response = await deleteEntry(selectedVersionsDetails);
        
        if (!response.success) {
          throw new Error(`Failed to delete versions: ${response.message}`);
        }
        
        // Başarılı silme sonrası state'i temizle
        setSelectedItems(new Set());
        
        // Success mesajı göster
        message.success('Selected versions deleted successfully.');
        
        // Dosya detayını yenile
        if (fileDetail?.id) {
          const updatedFileDetail = await getFileById(fileDetail.id);
          if (updatedFileDetail.success && updatedFileDetail.data) {
            setFileDetail(updatedFileDetail.data);
          }
        }
      } else {
        // Seçili versiyon yoksa mevcut dosyayı sil
        
        if (!fileDetail?.id) {
          throw new Error('No file to delete');
        }
        
        const response = await deleteEntry([{
          id: fileDetail.id,
          entryType: fileDetail.entryType
        }]);
        
        if (!response.success) {
          throw new Error(`Failed to delete file: ${response.message}`);
        }
        
        // Success mesajı göster
        message.success(`${fileDetail.name} deleted successfully.`);
        
        // Geri git
        if (onBack) {
          onBack();
        } else {
          navigate(getProjectsBasePath());
        }
      }
      
    } catch (error) {
      throw error; // FooterActions'da handle edilecek
    }
  };

  const handleBulkDownload = async () => {
    if (selectedItems.size === 0) {
      message.error('No version selected for download.');
      return;
    }

    try {
      // Seçili versiyonları filtrele
      const selectedVersions = fileDetail?.versions?.filter((version: any) => 
        selectedItems.has(version.id)
      ) || [];
      
      if (selectedVersions.length === 0) {
        message.error('No downloadable versions found in selection.');
        return;
      }

      for (const version of selectedVersions) {
        try {
          const downloadUrl = version.fileUrl;
          
          if (downloadUrl) {
            const versionName = version.name || `Version_${version.id}`;
            const fileExtension = downloadUrl.split('.').pop() || 'mp4';
            const fullFileName = `${versionName}.${fileExtension}`;
            
            const response = await fetch(downloadUrl);
            if (response.ok) {
              const blob = await response.blob();
              const url = window.URL.createObjectURL(blob);
              
              const link = document.createElement('a');
              link.href = url;
              link.download = fullFileName;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              
              window.URL.revokeObjectURL(url);
              
              message.success(`${fullFileName} download started.`);
            } else {
              message.error(`Failed to download ${versionName}.`);
            }
          } else {
            message.error(`Download URL not available for ${version.name || `Version ${version.id}`}.`);
          }
        } catch (versionError) {
          message.error(`Error downloading ${version.name || `Version ${version.id}`}.`);
          console.error('Version download error:', versionError);
        }
      }
    } catch (error) {
      message.error('An error occurred during download.');
      console.error('Bulk download error:', error);
    }
  };

  const handleBulkCopyUrl = async () => {
    try {
      if (selectedItems.size > 0) {

        
        // Seçili versiyonların URL'lerini oluştur
        const urls = fileDetail?.versions
          ?.filter((version: any) => selectedItems.has(version.id))
          .map((version: any) => {
            const versionPath = `${window.location.pathname}#v${version.id}`;
            return `${window.location.origin}${versionPath}`;
          }) || [];
        
        if (urls.length > 0) {
          const urlText = urls.join('\n');
          await navigator.clipboard.writeText(urlText);
          message.success(`URLs of ${urls.length} versions copied.`);
        }
      } else {
        // Seçili versiyon yoksa mevcut sayfanın URL'sini kopyala
        const currentUrl = window.location.href;
        await navigator.clipboard.writeText(currentUrl);
                  message.success('Page URL copied.');
      }
    } catch (error) {
              message.error('An error occurred while copying URL.');
    }
  };

  const handleRefresh = async () => {
    if (fileDetail?.id) {
      const updatedFileDetail = await getFileById(fileDetail.id);
      if (updatedFileDetail.success && updatedFileDetail.data) {
        setFileDetail(updatedFileDetail.data);
      }
    }
  };



  const formatDate = (dateString: string) => {
    if (!dateString || typeof dateString !== 'string') return "-";
    
    // API'den gelen tarih GMT+0 (UTC) formatında
    // Eğer 'Z' yoksa UTC olduğunu belirtmek için ekleyelim
    const utcDateString = dateString.includes('Z') ? dateString : dateString + 'Z';
    
    // UTC tarihi parse et ve local time'a çevir
    const date = new Date(utcDateString);
    
    // Eğer tarih geçersizse
    if (isNaN(date.getTime())) {
      return "-";
    }
    
    return date.toLocaleDateString(undefined, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getFileType = (fileName: string, videoUrl?: string) => {
    let extension: string | undefined = undefined;
    if (fileName && fileName.includes('.')) {
      const parts = fileName.split('.');
      if (parts.length > 1) {
        extension = parts.pop()?.toLowerCase();
      }
    }
    if ((!extension || extension === fileName.toLowerCase()) && videoUrl) {
      const cleanUrl = videoUrl.split('?')[0].split('#')[0];
      const urlExtension = cleanUrl.split('.').pop()?.toLowerCase().trim();
      if (urlExtension && urlExtension.length <= 4) {
        const cleanExtension = urlExtension.replace(/[^a-z0-9]/gi, '');
        if (cleanExtension) {
          extension = cleanExtension;
        }
      }
    }
    if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm'].includes(extension || '')) {
      return 'video';
    } else if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg'].includes(extension || '')) {
      return 'image';
    } else if (['pdf'].includes(extension || '')) {
      return 'pdf';
    } else if (['doc', 'docx'].includes(extension || '')) {
      return 'document';
    } else {
      return 'other';
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 20px' }}>
        <div>{t('fileDetail.loading')}</div>
      </div>
    );
  }

  if (!fileDetail) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 20px' }}>
        <div>{t('fileDetail.fileNotFound')}</div>
        <Button onClick={handleGoBack} style={{ marginTop: '16px' }}>
          {t('fileDetail.goBack')}
        </Button>
      </div>
    );
  }

  const versions = fileDetail.versions || [];
  

  return (
    <>
      <div className={styles.newEntryContainer}>
        <div className={styles.newEntryHeader}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
            <Button icon={<ArrowLeftOutlined />} onClick={handleGoBack}>
              Back
            </Button>
          </div>
        </div>
      </div>

      <div className={styles.projectGrid}>
        {versions.length > 0 ? (
          versions.map((version: any) => {
            const fileUrl = version.fileUrl || version.url || version.downloadUrl || version.path || '';
            const fileType = getFileType(fileDetail.name, fileUrl);
            
            return (
              <FolderCard
                key={version.id}
                folderType="file"
                selectable={true}
                selected={selectedItems.has(version.id)}
                onSelectChange={(checked) => handleItemSelect(version.id, checked)}
                style={{ margin: 0, cursor: 'pointer' }}
                onClick={() => handleVersionClick(version)}
              >
                <div className={styles.gradientBorderBox}>
                  <div className={styles.gradientBorderBoxContent}>
                    {version.name || `Version ${version.id}`}
                  </div>
                </div>
                
                <div className={styles.projectImage}>
                  {fileType === 'image' && fileUrl ? (
                    <img src={fileUrl} alt={version.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : fileType === 'video' && fileUrl ? (
                    <video 
                      src={fileUrl} 
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      muted
                      onLoadedData={(e) => {
                        // Video thumbnail oluştur
                        const video = e.target as HTMLVideoElement;
                        video.currentTime = 1;
                      }}
                    />
                  ) : (
                    <ImagePlaceholder style={{ width: "100%", height: "100%" }} />
                  )}
                </div>
                
                <div className={styles.projectDetailINfo}>
                  <div 
                    className={styles.status} 
                    style={{ 
                      backgroundColor: getStatusColor(version.status), 
                      color: '#fff', 
                      padding: '4px 8px', 
                      borderRadius: '4px', 
                      fontSize: '12px', 
                      fontWeight: 500 
                    }}
                  >
                    {getStatusLabel(version.status)}
                  </div>
                  <div style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>
                    {fileType.toUpperCase()}
                  </div>
                </div>
                
                <div className={styles.projectFooter}>
                  <div className={styles.gradientBorderTop}>
                    <span className={styles.date}>
                      {formatDate(version.createdAt)}
                    </span>
                  </div>
                </div>
              </FolderCard>
            );
          })
        ) : (
          <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
            No versions found for this file.
          </div>
        )}
      </div>

      {/* FooterActions - Sadece seçim yapıldığında göster */}
      {selectedItems.size > 0 && (
        <FooterActions
          onDelete={handleBulkDelete}
          onDownload={handleBulkDownload}
          onCopyUrl={handleBulkCopyUrl}
          onStatusChange={handleRefresh} // Statü değişikliği sonrası sayfayı yenile
          fileDetail={fileDetail}
          selectedItems={selectedItems}
          selectedItemsDetails={
            fileDetail?.versions && Array.isArray(fileDetail.versions)
              ? fileDetail.versions
                  .filter((version: any) => selectedItems.has(version.id))
                  .map((version: any) => ({
                    id: version.id,
                    name: version.name || `Version ${version.id}`,
                    entryType: 'File',
                    status: version.status,
                    share: version.share || []
                  }))
              : []
          }
          style={{
            transform: 'translateY(0)',
            transition: 'transform 0.3s ease-in-out, opacity 0.3s ease-in-out',
            opacity: 1
          }}
        />
      )}
      
      {/* Footer Actions için safe alan */}
      <div className={styles.footerSafeArea} />
    </>
  );
};

export default FileVersions; 